/**
 * useDataLayer - Hook for data layer components to interact with MapController and registry.
 * Use in any component that is registered as a data layer (RegistryComponentProps).
 * Provides layer on/off, default values, and merged registry data (defaultValues + injections).
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useMap } from '../MapContext';
import { getRegistryData } from '../../../pages/registry/componentRegistry';

/**
 * @param {import('../../../pages/registry/componentRegistry').RegistryComponentProps} props - From DynamicComponent (componentSlug, componentName, defaultValues, injections).
 * @returns {{
 *   slug: string;
 *   mapController: import('../MapController').default | null;
 *   isReady: boolean;
 *   isActive: boolean;
 *   setActive: (active: boolean) => Promise<void>;
 *   setLayerDefaultValues: (key: string, value: unknown) => Promise<void>;
 *   data: Record<string, unknown>;
 * }}
 */
export function useDataLayer(props) {
  const { componentSlug, componentName, defaultValues = null, injections } = props;
  const { layerController: mapController } = useMap();
  const slug = componentSlug;

  const [isActive, setIsActive] = useState(false);
  const prevActiveRef = useRef(false);
  const userActionRef = useRef(false);

  const isReady = Boolean(mapController?.isReady?.());

  // Merged registry data (defaultValues + injections) for component use
  const data = getRegistryData(props);

  // Sync isActive with MapController on mount and when map becomes ready
  useEffect(() => {
    if (!mapController || !mapController.isReady()) return;
    const initPromise = mapController.getInitializationPromise?.();
    if (initPromise) {
      initPromise.then(() => {
        if (!mapController?.isReady()) return;
        const active = mapController.isLayerActive(slug);
        if (active !== prevActiveRef.current && !userActionRef.current) {
          prevActiveRef.current = active;
          setIsActive(active);
        }
      });
      return;
    }
    const active = mapController.isLayerActive(slug);
    if (active !== prevActiveRef.current && !userActionRef.current) {
      prevActiveRef.current = active;
      setIsActive(active);
    }
  }, [mapController, slug]);

  // Re-sync isActive when layers are initialized (e.g. LEK with defaults added by initializeLayers).
  // The hidden instance may have mounted before initializeLayers ran, so its first sync saw the layer as inactive.
  useEffect(() => {
    if (!mapController?.isReady()) return;
    const onLayersInitialized = () => {
      if (!mapController?.isReady()) return;
      const active = mapController.isLayerActive(slug);
      if (active !== prevActiveRef.current && !userActionRef.current) {
        prevActiveRef.current = active;
        setIsActive(active);
      }
    };
    window.addEventListener('mapControllerLayersInitialized', onLayersInitialized);
    return () => window.removeEventListener('mapControllerLayersInitialized', onLayersInitialized);
  }, [mapController, slug]);

  // Also sync when our specific layer is added/removed (mapControllerLayerChanged).
  // Catches timing cases where we mount after mapControllerLayersInitialized fired.
  useEffect(() => {
    if (!mapController?.isReady()) return;
    const onLayerChanged = (e) => {
      const { slug: changedSlug, active } = e.detail || {};
      if (changedSlug !== slug) return;
      if (active !== prevActiveRef.current && !userActionRef.current) {
        prevActiveRef.current = active;
        setIsActive(active);
      }
    };
    window.addEventListener('mapControllerLayerChanged', onLayerChanged);
    return () => window.removeEventListener('mapControllerLayerChanged', onLayerChanged);
  }, [mapController, slug]);

  useEffect(() => {
    if (!mapController || !mapController.isReady()) return;
    const active = mapController.isLayerActive(slug);
    if (active !== prevActiveRef.current) {
      prevActiveRef.current = active;
      if (!userActionRef.current) setIsActive(active);
    }
  });

  const setActive = useCallback(async (active) => {
    if (!mapController || !mapController.isReady()) return;
    userActionRef.current = true;
    try {
      if (active) {
        await mapController.addLayer(slug);
        setIsActive(true);
        prevActiveRef.current = true;
      } else {
        await mapController.removeLayer(slug);
        setIsActive(false);
        prevActiveRef.current = false;
      }
    } finally {
      userActionRef.current = false;
    }
  }, [mapController, slug]);

  const setLayerDefaultValues = useCallback(async (key, value) => {
    if (!mapController || !mapController.isReady()) return;
    await mapController.setLayerDefaultValues(slug, key, value);
  }, [mapController, slug]);

  return {
    slug,
    componentName,
    mapController,
    isReady,
    isActive,
    setActive,
    setLayerDefaultValues,
    data,
  };
}
